import { useEffect, useState } from 'preact/hooks'
import { Loader2, RefreshCcw, Star, Trash2 } from 'lucide-preact'
import { sendingDomainsApi } from '@/api/sendingDomains'
import { ConfirmationModal } from '@/components/modals/ConfirmationModal'
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock'
import { useCompany } from '@/hooks/useCompany'
import { useConfirmation } from '@/hooks/useConfirmation'
import { useDomainVerification } from '@/hooks/useDomainVerification'
import { useToast } from '@/hooks/useToast'
import { sendingDomainPill, type SendingDomainViewDto, type SendingDomainWarmupSummaryDto } from '@/types/sendingDomains'
import { getDisplayErrorMessages } from '@/utils/errorHandling'
import { formatCount, localShortDay } from '@/utils/sendingDomainWarmup'
import {
  checkedAtPhrase,
  domainPageSubtitle,
  readyVerdict,
  regionLabel,
  restartConfirmation,
  resumeConfirmation,
} from '@/utils/sendingDomainStatus'
import { AddDomainForm } from './AddDomainForm'
import { DomainOverlay } from './DomainOverlay'
import { DomainStatusBox } from './DomainPageParts'
import { DomainSetupView } from './DomainSetupView'
import { DomainStatePill } from './DomainStatePill'
import { DomainStoppedView } from './DomainStoppedView'
import { DomainSuggestions } from './DomainSuggestions'
import { DomainTechnicalDetails } from './DomainTechnicalDetails'
import { DomainWarmingView } from './DomainWarmingView'
import { useDomainWarmup } from './useDomainWarmup'
import { DANGER_GHOST_BUTTON, PRIMARY_BUTTON, SOFT_BUTTON } from './styles'

// Domain is "actively verifying" (poll + show Checking…) only in these states — not NotStarted,
// where Resend hasn't been asked to check anything yet, and not the terminal states.
const VERIFYING_STATUSES = ['Pending', 'PartiallyVerified']

type Busy = 'check' | 'default' | 'remove' | null

interface AddSendingDomainPanelProps {
  isOpen: boolean
  clientCompanyId: string
  /** Existing domain to manage, or null to add a new one. */
  domain: SendingDomainViewDto | null
  /** The company's domains, so a domain failing its website check can point at a warming subdomain instead. */
  domains: SendingDomainViewDto[]
  summary: SendingDomainWarmupSummaryDto | null
  onClose: () => void
  onSaved: (domain: SendingDomainViewDto) => void
  onDeleted: (id: string) => void
  onOpenDomain: (domain: SendingDomainViewDto) => void
}

/**
 * The full-screen sending-domain view: adding one, then managing it through its warm-up. The page follows the
 * domain's state (Setting up, Warming, Holding, Stopped, Ready); DNS records and tracking sit folded under it.
 * Refusals stay on the page beside the action, not in a toast.
 */
export function AddSendingDomainPanel({
  isOpen, clientCompanyId, domain, domains, summary, onClose, onSaved, onDeleted, onOpenDomain,
}: AddSendingDomainPanelProps) {
  const { showSuccess } = useToast()
  const { company } = useCompany()
  const companyDomain = company?.CanonicalDomain || null
  const confirmation = useConfirmation()
  const { confirmationState, showConfirmation, hideConfirmation } = confirmation
  // Domain shown in detail mode: the prop (manage) or the just-created one.
  const [activeDomain, setActiveDomain] = useState<SendingDomainViewDto | null>(domain)
  const [busy, setBusy] = useState<Busy>(null)
  const [actionError, setActionError] = useState<string[] | null>(null)

  useBodyScrollLock(isOpen)

  useEffect(() => {
    if (!isOpen) return
    setActiveDomain(domain)
    setBusy(null)
    setActionError(null)
    hideConfirmation()
  }, [isOpen, domain])

  // Only auto-poll once verification is actually underway (Verify clicked → Pending). Avoids the
  // misleading perpetual "Checking…" spinner on a freshly-added, Not-started domain.
  const isVerifying = !!activeDomain && VERIFYING_STATUSES.includes(activeDomain.Status)
  const verification = useDomainVerification(clientCompanyId, activeDomain, isOpen && isVerifying)
  const shown = verification.status ?? activeDomain
  const pill = shown ? sendingDomainPill(shown) : null
  // A restart keeps the state Warming, so the step and start time are part of what makes the figures stale.
  const warmupKey = shown ? `${shown.State}|${shown.WarmupStage}|${shown.WarmupStartedAtUtc}|${shown.StoppedAtUtc}` : ''
  const warmup = useDomainWarmup(
    clientCompanyId, shown?.Id ?? null, warmupKey, isOpen && (pill === 'Warming' || pill === 'Holding' || pill === 'Stopped'))

  if (!isOpen) return null

  if (!activeDomain || !shown) {
    return (
      <AddDomainForm
        clientCompanyId={clientCompanyId}
        companyDomain={companyDomain}
        summary={summary}
        onCreated={created => { setActiveDomain(created); onSaved(created) }}
        onClose={onClose}
      />
    )
  }

  const saved = (updated: SendingDomainViewDto) => {
    setActiveDomain(updated)
    onSaved(updated)
  }
  const working = busy !== null || !!confirmationState.loading

  const checkAgain = async () => {
    setBusy('check')
    setActionError(null)
    const res = await sendingDomainsApi.verify(clientCompanyId, shown.Id)
    setBusy(null)
    if (res.error || !res.data) {
      setActionError(getDisplayErrorMessages(res.error, 'The check could not run.'))
      return
    }
    saved(res.data)
  }

  const setDefault = async () => {
    setBusy('default')
    setActionError(null)
    const res = await sendingDomainsApi.setDefault(clientCompanyId, shown.Id)
    setBusy(null)
    if (res.error || !res.data) {
      setActionError(getDisplayErrorMessages(res.error, 'The default could not be changed.'))
      return
    }
    saved(res.data)
    showSuccess('Default updated', `${res.data.Domain} is now your default sending domain.`)
  }

  const remove = async () => {
    setBusy('remove')
    setActionError(null)
    const res = await sendingDomainsApi.remove(clientCompanyId, shown.Id)
    setBusy(null)
    if (res.error) {
      setActionError(getDisplayErrorMessages(res.error, 'The domain could not be removed.'))
      return
    }
    onDeleted(shown.Id)
    showSuccess('Domain removed', `${shown.Domain} was removed.`)
    onClose()
  }

  const askRestart = () => {
    const steps = warmup.warmup?.Steps
    if (!steps) return
    const { question, detail } = restartConfirmation(steps)
    showConfirmation({
      title: question,
      message: detail,
      type: 'warning',
      confirmText: 'Restart warm-up',
      onConfirm: async () => {
        setActionError(null)
        const res = await sendingDomainsApi.restartWarmup(clientCompanyId, shown.Id)
        if (res.error || !res.data) {
          setActionError(getDisplayErrorMessages(res.error, 'The warm-up could not be restarted.'))
          return
        }
        saved(res.data)
        showSuccess('Warm-up restarted', `${res.data.Domain} is back at step ${res.data.WarmupStage}.`)
      },
    })
  }

  const askResume = () => {
    const stopped = warmup.warmup?.Stopped
    if (!stopped) return
    const resumeDaily = formatCount(stopped.ResumeDailyLimit)
    const { question, detail } = resumeConfirmation(shown.Domain, stopped, shown.WarmupLastStep)
    showConfirmation({
      title: question,
      message: detail,
      type: 'info',
      confirmText: `Resume at ${resumeDaily} a day`,
      onConfirm: async () => {
        setActionError(null)
        const res = await sendingDomainsApi.resume(clientCompanyId, shown.Id)
        if (res.error || !res.data) {
          setActionError(getDisplayErrorMessages(res.error, 'The domain could not be resumed.'))
          return
        }
        saved(res.data)
        showSuccess('Sending resumed', `${res.data.Domain} is sending again, up to ${resumeDaily} a day.`)
      },
    })
  }

  const refresh = () => {
    void verification.refresh()
    void warmup.reload()
  }

  const onEscape = () => {
    if (confirmationState.isOpen) {
      if (!confirmationState.loading) hideConfirmation()
      return
    }
    if (!working) onClose()
  }

  const errors = [...(actionError ?? []), ...(verification.error ? getDisplayErrorMessages(verification.error) : [])]

  const footer = (
    <div className="space-y-3">
      {errors.length > 0 && (
        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
          {errors.map(message => <p key={message}>{message}</p>)}
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <button type="button" onClick={() => void remove()} disabled={working} className={DANGER_GHOST_BUTTON}>
          {busy === 'remove' ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Trash2 className="h-4 w-4" aria-hidden="true" />} Remove
        </button>
        {pill === 'SettingUp' ? (
          <button type="button" onClick={() => void checkAgain()} disabled={working} className={PRIMARY_BUTTON}>
            {busy === 'check'
              ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              : <RefreshCcw className="h-4 w-4" aria-hidden="true" />}
            Check again
          </button>
        ) : (
          <div className="flex items-center gap-2">
            {shown.Status === 'Verified' && (shown.IsDefault ? (
              <span className="inline-flex items-center gap-1.5 rounded-lg bg-slate-100 px-3 py-2 text-sm font-medium text-slate-700 dark:bg-slate-700 dark:text-slate-200">
                <Star className="h-4 w-4 fill-current" aria-hidden="true" /> Default
              </span>
            ) : (
              <button
                type="button"
                onClick={() => void setDefault()}
                disabled={working}
                title="Use this domain by default on new activations and test sends"
                className={SOFT_BUTTON}
              >
                {busy === 'default' ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Star className="h-4 w-4" aria-hidden="true" />}
                Set as default
              </button>
            ))}
            <button type="button" onClick={refresh} disabled={verification.isLoading || working} title="Re-check status" className={SOFT_BUTTON}>
              <RefreshCcw className={`h-4 w-4 ${verification.isLoading ? 'animate-spin' : ''}`} aria-hidden="true" /> Refresh
            </button>
          </div>
        )}
      </div>
    </div>
  )

  return (
    // Keyed by domain, so opening another domain from this one starts at its top with focus in its body.
    <DomainOverlay key={shown.Id} title={shown.Domain} subtitle={domainPageSubtitle(shown)} onEscape={onEscape} onClose={onClose} footer={footer}>
      <div className="space-y-10">
        {pill === 'SettingUp' && (
          <DomainSetupView
            clientCompanyId={clientCompanyId}
            domain={shown}
            domains={domains}
            companyDomain={companyDomain}
            summary={summary}
            checking={verification.isPolling}
            onOpenDomain={onOpenDomain}
            onSaved={saved}
          />
        )}
        {(pill === 'Warming' || pill === 'Holding') && (
          <DomainWarmingView domain={shown} warmup={warmup.warmup} warmupError={warmup.error} busy={working} onRestart={askRestart} />
        )}
        {pill === 'Stopped' && (
          <DomainStoppedView
            clientCompanyId={clientCompanyId}
            domain={shown}
            warmup={warmup.warmup}
            warmupError={warmup.error}
            busy={working}
            onResume={askResume}
            onRestart={askRestart}
          />
        )}
        {pill === 'Ready' && <ReadyView domain={shown} summary={summary} />}
        <DomainTechnicalDetails clientCompanyId={clientCompanyId} domain={shown} onUpdated={saved} />
      </div>

      <ConfirmationModal
        isOpen={confirmationState.isOpen}
        title={confirmationState.title}
        message={confirmationState.message}
        type={confirmationState.type}
        confirmText={confirmationState.confirmText}
        loading={confirmationState.loading}
        onConfirm={() => void confirmation.handleConfirm()}
        onCancel={hideConfirmation}
      />
    </DomainOverlay>
  )
}

/** A domain that finished warm-up: no limits, but the automatic stop still watches it. */
function ReadyView({ domain, summary }: { domain: SendingDomainViewDto; summary: SendingDomainWarmupSummaryDto | null }) {
  const facts = [
    { label: 'Status', value: <DomainStatePill domain={domain} /> },
    { label: 'Region', value: regionLabel(domain.Region) },
    ...(domain.WarmupCompletedAtUtc ? [{ label: 'Warm-up completed', value: localShortDay(domain.WarmupCompletedAtUtc) }] : []),
    ...(domain.LastCheckedUtc ? [{ label: 'Last checked', value: checkedAtPhrase(domain.LastCheckedUtc) }] : []),
  ]
  return (
    <div className="space-y-10">
      <DomainStatusBox pill="Ready" verdict={readyVerdict(summary?.RateLimits ?? null)} facts={facts} />
      <DomainSuggestions domain={domain.Domain} />
    </div>
  )
}
